import React from 'react';
import { Typography } from 'antd';

interface ProspectorArtifactSnapshotProps {
  payload: unknown;
}

type SnapshotFields = {
  title?: string;
  name?: string;
  company?: string;
  objective?: string;
  entropy_estimate?: number | string;
  entropy_label?: string;
  entropy_description?: string;
  primary_constraint?: string;
  primary_constraint_area?: string;
  constraint_meaning?: string;
  unlocks?: string;
  what_this_unlocks?: string;
};

function asRecord(payload: unknown): Record<string, unknown> | null {
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    return payload as Record<string, unknown>;
  }
  if (typeof payload === 'string') {
    try {
      const parsed = JSON.parse(payload) as unknown;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return null;
    }
  }
  return null;
}

function pickString(obj: Record<string, unknown>, ...keys: string[]): string | undefined {
  for (const key of keys) {
    const v = obj[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return undefined;
}

function pickNumberish(obj: Record<string, unknown>, ...keys: string[]): number | undefined {
  for (const key of keys) {
    const v = obj[key];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v.trim()) {
      const n = Number(v);
      if (Number.isFinite(n)) return n;
    }
  }
  return undefined;
}

function normalizeSnapshot(payload: unknown): SnapshotFields | null {
  const obj = asRecord(payload);
  if (!obj) return null;

  const nested =
    asRecord(obj.prospect_snapshot) ||
    asRecord(obj.snapshot) ||
    asRecord(obj.artifact_payload) ||
    obj;

  const objective = pickString(nested, 'objective', 'stated_objective');
  const primary_constraint = pickString(
    nested,
    'primary_constraint',
    'primaryConstraint',
    'constraint'
  );
  const entropy_estimate = pickNumberish(nested, 'entropy_estimate', 'entropyEstimate', 'entropy');
  const unlocks = pickString(nested, 'unlocks', 'what_this_unlocks', 'whatThisUnlocks');

  // Only treat as structured Prospect Snapshot when core fields are present.
  if (!objective && !primary_constraint && entropy_estimate == null) {
    return null;
  }

  return {
    title: pickString(nested, 'title', 'surface_name') || 'Business snapshot',
    name: pickString(nested, 'name', 'prospect_name', 'person_name'),
    company: pickString(nested, 'company', 'company_name'),
    objective,
    entropy_estimate,
    entropy_label: pickString(nested, 'entropy_label', 'entropy_narrative', 'path_label'),
    entropy_description: pickString(
      nested,
      'entropy_description',
      'path_description',
      'where_you_are'
    ),
    primary_constraint,
    primary_constraint_area: pickString(
      nested,
      'primary_constraint_area',
      'constraint_area'
    ),
    constraint_meaning: pickString(nested, 'constraint_meaning', 'constraint_detail'),
    unlocks,
  };
}

function entropyBarColor(score: number | undefined): string {
  if (score == null) return '#8c8c8c';
  if (score <= 3) return '#cf1322';
  if (score <= 6) return '#d48806';
  return '#389e0d';
}

function entropyWidthPct(score: number | undefined): number {
  if (score == null) return 0;
  return Math.max(0, Math.min(100, (score / 9) * 100));
}

/**
 * Renders Krybot `artifact_payload` as a Prospect Snapshot–shaped surface when
 * structured fields are present; otherwise falls back to readable JSON/text.
 * SPA substitute for skill `show_widget` (no Claude Visualizer in web chat).
 */
export const ProspectorArtifactSnapshot: React.FC<ProspectorArtifactSnapshotProps> = ({
  payload,
}) => {
  if (payload === undefined || payload === null) {
    return null;
  }

  const snap = normalizeSnapshot(payload);
  if (snap) {
    const who = [snap.name, snap.company].filter(Boolean).join(', ');
    const barColor = entropyBarColor(
      typeof snap.entropy_estimate === 'number' ? snap.entropy_estimate : undefined
    );
    return (
      <div
        style={{
          marginBottom: 12,
          textAlign: 'left',
          maxWidth: 600,
          fontFamily: 'inherit',
        }}
        data-testid="prospector-artifact-snapshot"
      >
        <Typography.Text
          type="secondary"
          style={{
            display: 'block',
            fontSize: 11,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            marginBottom: 12,
          }}
        >
          {snap.title}
          {who ? ` — ${who}` : ''}
        </Typography.Text>

        {snap.objective && (
          <section
            style={{
              border: '1px solid #f0f0f0',
              borderRadius: 12,
              padding: 16,
              marginBottom: 12,
              background: '#fff',
            }}
          >
            <Typography.Text
              type="secondary"
              style={{
                display: 'block',
                fontSize: 11,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                marginBottom: 6,
              }}
            >
              Objective
            </Typography.Text>
            <Typography.Paragraph style={{ margin: 0, fontSize: 15, fontWeight: 500 }}>
              {snap.objective}
            </Typography.Paragraph>
          </section>
        )}

        {(snap.entropy_estimate != null || snap.entropy_label || snap.entropy_description) && (
          <section
            style={{
              border: '1px solid #f0f0f0',
              borderRadius: 12,
              padding: 16,
              marginBottom: 12,
              background: '#fff',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 8,
              }}
            >
              <Typography.Text
                type="secondary"
                style={{
                  fontSize: 11,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                }}
              >
                Where you are now
              </Typography.Text>
              {(snap.entropy_label || snap.entropy_estimate != null) && (
                <span style={{ fontSize: 13, fontWeight: 500, color: barColor }}>
                  {snap.entropy_label ||
                    (snap.entropy_estimate != null ? `Path mark ${snap.entropy_estimate}` : '')}
                </span>
              )}
            </div>
            {snap.entropy_estimate != null && (
              <div
                style={{
                  background: '#f5f5f5',
                  borderRadius: 4,
                  height: 6,
                  marginBottom: snap.entropy_description ? 8 : 0,
                }}
              >
                <div
                  style={{
                    background: barColor,
                    borderRadius: 4,
                    height: 6,
                    width: `${entropyWidthPct(
                      typeof snap.entropy_estimate === 'number'
                        ? snap.entropy_estimate
                        : undefined
                    )}%`,
                  }}
                />
              </div>
            )}
            {snap.entropy_description && (
              <Typography.Paragraph type="secondary" style={{ margin: 0, fontSize: 13 }}>
                {snap.entropy_description}
              </Typography.Paragraph>
            )}
          </section>
        )}

        {snap.primary_constraint && (
          <section
            style={{
              background: '#FCEBEB',
              border: '1px solid #E24B4A',
              borderRadius: 12,
              padding: 16,
              marginBottom: 12,
            }}
          >
            <Typography.Text
              style={{
                display: 'block',
                fontSize: 11,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: '#A32D2D',
                marginBottom: 6,
              }}
            >
              Primary constraint
            </Typography.Text>
            <Typography.Paragraph
              style={{ margin: 0, marginBottom: 6, fontSize: 14, fontWeight: 500, color: '#A32D2D' }}
            >
              {snap.primary_constraint_area
                ? `${snap.primary_constraint_area} — ${snap.primary_constraint}`
                : snap.primary_constraint}
            </Typography.Paragraph>
            {snap.constraint_meaning && (
              <Typography.Paragraph style={{ margin: 0, fontSize: 13, color: '#A32D2D' }}>
                {snap.constraint_meaning}
              </Typography.Paragraph>
            )}
          </section>
        )}

        {snap.unlocks && (
          <section
            style={{
              background: '#EAF3DE',
              border: '1px solid #97C459',
              borderRadius: 12,
              padding: 16,
              marginBottom: 4,
            }}
          >
            <Typography.Text
              style={{
                display: 'block',
                fontSize: 11,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: '#3B6D11',
                marginBottom: 6,
              }}
            >
              What this unlocks
            </Typography.Text>
            <Typography.Paragraph style={{ margin: 0, fontSize: 13, color: '#3B6D11' }}>
              {snap.unlocks}
            </Typography.Paragraph>
          </section>
        )}
      </div>
    );
  }

  const text = typeof payload === 'string' ? payload : JSON.stringify(payload, null, 2);
  return (
    <div
      style={{
        marginBottom: 8,
        textAlign: 'left',
        border: '1px solid #f0f0f0',
        borderRadius: 8,
        padding: 12,
        background: '#fff',
      }}
      data-testid="prospector-artifact-snapshot-raw"
    >
      <Typography.Text
        type="secondary"
        style={{
          display: 'block',
          fontSize: 11,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          marginBottom: 8,
        }}
      >
        Structured snapshot
      </Typography.Text>
      <Typography.Paragraph style={{ margin: 0 }} type="secondary">
        <pre
          style={{
            margin: 0,
            whiteSpace: 'pre-wrap',
            fontSize: 12,
            maxHeight: 280,
            overflow: 'auto',
            fontFamily: 'inherit',
          }}
        >
          {text}
        </pre>
      </Typography.Paragraph>
    </div>
  );
};
